// Music theory utilities and helper functions for Meradio
import type { AudioFeatures, ElectronicGenre } from '@meradio/shared-types';

// ========================
// Musical Key Utilities
// ========================

export const MUSICAL_KEYS = [
  'C', 'C♯/D♭', 'D', 'D♯/E♭', 'E', 'F', 
  'F♯/G♭', 'G', 'G♯/A♭', 'A', 'A♯/B♭', 'B'
] as const;

export const CAMELOT_WHEEL = [
  '8B', '3B', '10B', '5B', '12B', '7B',
  '2B', '9B', '4B', '11B', '6B', '1B',
  '5A', '12A', '7A', '2A', '9A', '4A',
  '11A', '6A', '1A', '8A', '3A', '10A'
] as const;

export function getMusicalKey(key: number, mode: number): string {
  if (key < 0 || key > 11) return 'Unknown';
  
  const keyName = MUSICAL_KEYS[key];
  const modeName = mode === 1 ? 'Major' : 'Minor';
  
  return `${keyName} ${modeName}`;
}

export function getCamelotKey(key: number, mode: number): string {
  if (key < 0 || key > 11) return '??';
  
  const offset = mode === 1 ? 0 : 12; // Major keys: 0-11, Minor keys: 12-23
  return CAMELOT_WHEEL[key + offset];
}

export function getCompatibleKeys(camelotKey: string): string[] {
  const number = Number(camelotKey.slice(0, -1));
  const letter = camelotKey.slice(-1);
  if (!Number.isInteger(number) || number < 1 || number > 12 || (letter !== 'A' && letter !== 'B')) {
    return [];
  }

  // Adjacency is by camelot number, not by the wheel array's pitch order
  const up = (number % 12) + 1;
  const down = ((number + 10) % 12) + 1;

  return [
    camelotKey,
    `${up}${letter}`,
    `${down}${letter}`,
    `${number}${letter === 'A' ? 'B' : 'A'}`,
  ];
}

// ========================
// BPM Utilities
// ========================

export function calculateBpmCompatibility(bpm1: number, bpm2: number) {
  const ratio = Math.max(bpm1, bpm2) / Math.min(bpm1, bpm2);
  const difference = Math.abs(bpm1 - bpm2);
  
  let compatibility: 'perfect' | 'good' | 'moderate' | 'difficult';
  let recommendation: string;
  
  if (difference <= 3) {
    compatibility = 'perfect';
    recommendation = 'Perfect for mixing without tempo adjustment';
  } else if (difference <= 6) {
    compatibility = 'good';
    recommendation = 'Good for mixing with minor tempo adjustment';
  } else if (ratio <= 1.5) {
    compatibility = 'moderate';
    recommendation = 'Requires significant tempo adjustment';
  } else {
    compatibility = 'difficult';
    recommendation = 'Consider using transition track or effects';
  }
  
  return {
    ratio,
    difference,
    compatibility,
    recommendation,
    targetBpm: Math.round((bpm1 + bpm2) / 2), // Suggested meeting point
  };
}

export function getBpmCategory(bpm: number): string {
  if (bpm < 70) return 'Very Slow';
  if (bpm < 90) return 'Slow';
  if (bpm < 110) return 'Moderate';
  if (bpm < 128) return 'Medium';
  if (bpm < 140) return 'Fast';
  if (bpm < 160) return 'Very Fast';
  if (bpm < 180) return 'Extremely Fast';
  return 'Ultra Fast';
}

// ========================
// Mood Analysis
// ========================

export function calculateMoodColor(features: AudioFeatures): string {
  const normalizedValues = {
    valence: features.valence,
    energy: features.energy,
    tempo: Math.min(features.tempo / 180.0, 1.0),
    key: features.key / 11.0,
  };
  
  const weights = { valence: 0.4, energy: 0.3, tempo: 0.2, key: 0.1 };
  
  const combinedValue = Object.entries(normalizedValues)
    .reduce((sum, [key, value]) => {
      return sum + ((value ?? 0) * weights[key as keyof typeof weights]);
    }, 0);
  
  return interpolateColor('#ff0000', '#00ff00', combinedValue);
}

function interpolateColor(color1: string, color2: string, factor: number): string {
  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);
  
  if (!c1 || !c2) return color1;
  
  const r = Math.round(c1.r + (c2.r - c1.r) * factor);
  const g = Math.round(c1.g + (c2.g - c1.g) * factor);
  const b = Math.round(c1.b + (c2.b - c1.b) * factor);
  
  return rgbToHex(r, g, b);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  } : null;
}

function rgbToHex(r: number, g: number, b: number): string {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

export function getMoodDescription(features: AudioFeatures): string {
  const { valence = 0.5, energy = 0.5 } = features;
  
  if (valence > 0.7 && energy > 0.7) return 'Euphoric & Energetic';
  if (valence > 0.7 && energy < 0.3) return 'Happy & Relaxed';
  if (valence < 0.3 && energy > 0.7) return 'Aggressive & Intense';
  if (valence < 0.3 && energy < 0.3) return 'Melancholic & Calm';
  if (valence > 0.5 && energy > 0.5) return 'Upbeat & Positive';
  if (valence < 0.5 && energy > 0.5) return 'Dark & Driving';
  if (valence > 0.5) return 'Pleasant & Moderate';
  return 'Moody & Atmospheric';
}

// ========================
// Genre Classification Utilities
// ========================

export function suggestGenresByFeatures(features: AudioFeatures): ElectronicGenre[] {
  const suggestions: ElectronicGenre[] = [];
  const { tempo, energy = 0, danceability = 0, valence = 0 } = features;
  
  // BPM-based suggestions
  if (tempo >= 120 && tempo <= 135 && energy > 0.6) {
    suggestions.push('House', 'Tech House', 'Deep House');
  }
  
  if (tempo >= 125 && tempo <= 135 && energy > 0.7) {
    suggestions.push('Techno (Peak Time/Driving)');
  }
  
  if (tempo >= 140 && tempo <= 150 && energy > 0.8) {
    suggestions.push('Hard Techno');
  }
  
  if (tempo >= 160 && tempo <= 180) {
    suggestions.push('Drum & Bass');
  }
  
  if (tempo >= 130 && tempo <= 140 && danceability > 0.7) {
    suggestions.push('Progressive House');
  }
  
  if (tempo <= 100 && valence < 0.5 && energy < 0.5) {
    suggestions.push('Organic House/Downtempo');
  }
  
  if (tempo >= 128 && tempo <= 132 && energy > 0.6 && danceability > 0.6) {
    suggestions.push('Bass House', 'Tech House');
  }
  
  // Remove duplicates and return first 3 suggestions
  return [...new Set(suggestions)].slice(0, 3);
}

// ========================
// Audio Analysis Utilities
// ========================

export function getAudioFeatureDescriptions(features: AudioFeatures) {
  return {
    tempo: `${Math.round(features.tempo)} BPM`,
    key: getMusicalKey(features.key, features.mode),
    camelotKey: getCamelotKey(features.key, features.mode),
    timeSignature: `${features.timeSignature}/4`,
    energy: `${Math.round((features.energy ?? 0) * 10)}/10`,
    valence: `${Math.round((features.valence ?? 0) * 10)}/10`,
    danceability: `${Math.round((features.danceability ?? 0) * 10)}/10`,
    acousticness: `${Math.round((features.acousticness ?? 0) * 100)}%`,
    instrumentalness: `${Math.round((features.instrumentalness ?? 0) * 100)}%`,
    liveness: `${Math.round((features.liveness ?? 0) * 100)}%`,
    speechiness: `${Math.round((features.speechiness ?? 0) * 100)}%`,
    loudness: `${Math.round((features.loudness ?? 0))} dB`,
    duration: formatDuration((features.durationMs ?? 0)),
    mood: getMoodDescription(features),
    bpmCategory: getBpmCategory(features.tempo),
  };
}

export function formatDuration(durationMs: number): string {
  const totalSeconds = Math.floor(durationMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

// ========================
// Mixing Compatibility
// ========================

export function calculateMixingCompatibility(
  features1: AudioFeatures,
  features2: AudioFeatures
) {
  const keyCompatibility = getCompatibleKeys(getCamelotKey(features1.key, features1.mode))
    .includes(getCamelotKey(features2.key, features2.mode));
    
  const bpmAnalysis = calculateBpmCompatibility(features1.tempo, features2.tempo);
  
  const energyDiff = Math.abs((features1.energy ?? 0) - (features2.energy ?? 0));
  const valenceDiff = Math.abs((features1.valence ?? 0) - (features2.valence ?? 0));
  
  let overallScore = 0;
  
  // Key compatibility (30% weight)
  if (keyCompatibility) overallScore += 30;
  
  // BPM compatibility (40% weight)
  if (bpmAnalysis.compatibility === 'perfect') overallScore += 40;
  else if (bpmAnalysis.compatibility === 'good') overallScore += 30;
  else if (bpmAnalysis.compatibility === 'moderate') overallScore += 15;
  
  // Energy compatibility (20% weight)
  if (energyDiff < 0.2) overallScore += 20;
  else if (energyDiff < 0.4) overallScore += 10;
  
  // Mood compatibility (10% weight)
  if (valenceDiff < 0.3) overallScore += 10;
  else if (valenceDiff < 0.5) overallScore += 5;
  
  let rating: 'excellent' | 'good' | 'fair' | 'poor';
  if (overallScore >= 80) rating = 'excellent';
  else if (overallScore >= 60) rating = 'good';
  else if (overallScore >= 40) rating = 'fair';
  else rating = 'poor';
  
  return {
    overallScore,
    rating,
    keyCompatibility,
    bpmAnalysis,
    energyDiff,
    valenceDiff,
    recommendations: generateMixingRecommendations(features1, features2, overallScore),
  };
}

function generateMixingRecommendations(
  features1: AudioFeatures,
  features2: AudioFeatures,
  score: number
): string[] {
  const recommendations: string[] = [];
  
  if (score >= 80) {
    recommendations.push('Excellent compatibility - perfect for seamless mixing');
  } else if (score < 40) {
    recommendations.push('Consider using a transition track or effects');
  }
  
  const bpmDiff = Math.abs(features1.tempo - features2.tempo);
  if (bpmDiff > 6) {
    recommendations.push(`Adjust tempo gradually - ${Math.round(bpmDiff)} BPM difference`);
  }
  
  const energyDiff = Math.abs((features1.energy ?? 0) - (features2.energy ?? 0));
  if (energyDiff > 0.4) {
    recommendations.push('Consider EQ adjustments to match energy levels');
  }
  
  if (!getCompatibleKeys(getCamelotKey(features1.key, features1.mode))
    .includes(getCamelotKey(features2.key, features2.mode))) {
    recommendations.push('Keys are not harmonically compatible - use with caution');
  }
  
  return recommendations;
}

export { parseTrackTitle, type ParsedTrackTitle } from './parseTrackTitle';

export const SHARP_KEYS = [
  'C', 'C#', 'D', 'D#', 'E', 'F',
  'F#', 'G', 'G#', 'A', 'A#', 'B'
] as const;

export function formatKeyShort(key: number, mode: number | string): string {
  if (key < 0 || key > 11) return '—';

  const keyName = SHARP_KEYS[key];
  if (typeof mode === 'string') {
    const lowered = mode.toLowerCase();
    if (lowered === 'major') return keyName;
    if (lowered === 'minor') return `${keyName}m`;
    return `${keyName} ${lowered.slice(0, 3)}`;
  }
  return mode === 1 ? keyName : `${keyName}m`;
}
